import { InformationBox } from '../../../miscellaneous-components/information-box'
import { IconName } from '../../../../../shared/interfaces/icon-picker.model'
import { FunctionComponent, SetStateAction, useEffect, useState } from 'react'
import { IGroupDetail } from '../../../../../shared/services/group/group.model'
import UserDetailsService from '../../../../../shared/services/user-details/user-details.service'
import { IUserDetails } from '../../../../../shared/services/user-details/user-details.model'
import { IHeadCell } from '../../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../../miscellaneous-components/table-display'
import { IReportAction } from '../../../../../shared/services/report-action/report-action.model'
import { Box, Button, Container, Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import { ReceiptLongOutlined } from '@mui/icons-material'
import { useMediaQuery } from '@mui/material'
import gaiaTheme from '../../../../../root-components/theme'
import { EmissionsActionCardHeading } from './emissions-action-card-heading'
import { EmissionsActionInputUtilities } from './emissions-action-input-utilities'
import { EmissionsActionInputGeneral } from './emissions-action-input-general'
import FileUploadService from '../../../../../shared/services/file-upload/file-upload.service'
import { BillType } from '../../../../../shared/services/file-upload/file-upload.model'
import { UploadBillDialog } from '../../../../dialogs/settings/data-import/upload-bill-dialog'

// The globally seeded group ids (db/seeders/add-the-groups-records) whose actions can be
// satisfied by uploading a bill, mapped to the kind of bill: vehicles run on fuel bills or
// fuel card statements, facilities and purchased energy on energy bills.
const BILL_GROUPS: { [groupId: string]: BillType } = {
  '22cc8b45-aa77-49aa-8fb2-c39bb884fa13': 'energy', // Company Facilities
  'acf9ae04-25d9-43d7-b442-fabd69e5844b': 'energy', // Purchased Energy
  '504daea8-fd6b-4b06-8e5f-dc1a90558ede': 'fuel',   // Company Vehicles
}

export const EmissionsActionsTab: FunctionComponent<{
  status: 'outstanding' | 'completed' | 'upcoming',
  getGroup: () => void,
  group: IGroupDetail,
  initialAction?: IReportAction,
  viewActions: 'actions' | 'input',
  setViewActions: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  setOrder: React.Dispatch<SetStateAction<any>>,
}> = ({
  status,
  group,
  initialAction,
  viewActions,
  setViewActions,
  getGroup,
  totalRecords,
  inputPage,
  setInputPage,
  perPage,
  setPerPage,
  setOrderBy,
  setOrder, }) => {
    //Services
    const userDetailsService = new UserDetailsService();
    //State
    const [activeReportAction, setActiveReportAction] = useState<IReportAction>()
    const [users, setUsers] = useState<IUserDetails[]>([]);
    const [billExtractionEnabled, setBillExtractionEnabled] = useState(false);
    const [billUploadAction, setBillUploadAction] = useState<IReportAction | null>(null);
    //Table State
    const [selected, setSelected] = useState<string[]>([]);
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
    // The bill kind this group's actions map to, when the group takes bills at all.
    const billType = BILL_GROUPS[group.id];
    //Use Effects
    // Deep link: the page resolved an action from the URL, show its form straight away.
    useEffect(() => {
      if (initialAction) {
        setActiveReportAction(initialAction)
      }
    }, [initialAction])

    useEffect(() => {
      userDetailsService.getAllUserDetails(`?status=active`)
        .then((response) => {
          setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    useEffect(() => {
      // Same gate as the import wizard: only offer bill upload where the environment has
      // extraction enabled, and only bother asking on bill-eligible groups.
      if (!billType) return;
      new FileUploadService().getBillExtractionStatus()
        .then(({ enabled }) => setBillExtractionEnabled(!!enabled))
        .catch(() => setBillExtractionEnabled(false))
    }, [billType])

    //Table Contents
    const headCells: IHeadCell[] = [];
    const rows = group.reportActions.actions.rows?.map((action: IReportAction) => {
      return {
        id: action.id,
        groupId: action.groupId,
        reportAction: action,
        status: status,
      }
    });

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };

    const showActionInputHandler = (reportAction: IReportAction) => {
      setActiveReportAction(reportAction);
      setViewActions('input');
    }

    return (
      <>
        {viewActions === 'actions' &&
          <>
            {group.reportActions.actions.rows.length > 0 && users ?
              <TableDisplay
                isMobileMode={true}
                isReportActionCard={true}
                headCells={headCells}
                rows={rows}
                selected={selected}
                onOrderChange={handleOrderChange}
                setSelected={setSelected}
                arrayForMobileComponent={users}
                functionOneForMobileComponent={showActionInputHandler}
                functionTwoForMobileComponent={getGroup}
                functionThreeForMobileComponent={billType && billExtractionEnabled && status === 'outstanding'
                  ? (reportAction: IReportAction) => setBillUploadAction(reportAction)
                  : undefined}
                inputPage={inputPage}
                setInputPage={setInputPage}
                totalRecords={totalRecords}
                perPage={perPage}
                setPerPage={setPerPage}
              /> :
              <Grid container>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <InformationBox
                    text={status === 'completed' ? `You have not ${status} any tasks` : `You have no ${status} tasks`}
                    icon={IconName.ErrorOutline}
                  />
                </Grid>
              </Grid>
            }
          </>}
        {viewActions === 'input' &&
          <Box sx={{ borderStyle: 'solid', borderColor: 'lightGrey.main', borderWidth: '1px', borderRadius: '10px', p: isMobileMode ? 2 : 4 }}>
            {activeReportAction &&
              <EmissionsActionCardHeading
                viewActions={viewActions}
                reportAction={activeReportAction}
                users={users}
                getGroup={getGroup}
              />}
            <Divider sx={{ mt: 2 }} />
            {billType && billExtractionEnabled && activeReportAction && !activeReportAction.completed &&
              <Container maxWidth='md' sx={{ pt: 3 }}>
                <Paper variant='outlined' sx={{ p: 2.5, borderRadius: '10px' }}>
                  <Stack direction='row' spacing={2} alignItems='center'>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '50%', backgroundColor: 'secondary.light', flexShrink: 0 }}>
                      <ReceiptLongOutlined color='primary' />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>Have the bill to hand?</Typography>
                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        Upload the PDF or a photo and the usage is read for you, linked to this task to review.
                      </Typography>
                    </Box>
                    <Button
                      variant='outlined'
                      startIcon={<ReceiptLongOutlined />}
                      onClick={() => setBillUploadAction(activeReportAction)}
                      sx={{ textTransform: 'none', flexShrink: 0 }}>
                      Upload bill
                    </Button>
                  </Stack>
                </Paper>
                <Divider sx={{ mt: 3 }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>or enter it yourself</Typography>
                </Divider>
              </Container>}
            <Container maxWidth='md' sx={{ pt: 2, pb: 4 }}>
              {activeReportAction && activeReportAction.actionCode === "utilities" &&
                <EmissionsActionInputUtilities
                  reportAction={activeReportAction}
                  groupId={group.id}
                  setViewActions={setViewActions}
                  getGroup={getGroup}
                />
              }
              {activeReportAction && activeReportAction.actionCode !== "utilities" &&
                <EmissionsActionInputGeneral
                  reportAction={activeReportAction}
                  categoryId={activeReportAction.categoryId}
                  groupId={group.id}
                  scope={group.scope}
                  setViewActions={setViewActions}
                  getGroup={getGroup}
                />
              }
            </Container>
          </Box>
        }
        {billType && billUploadAction &&
          <UploadBillDialog
            open={!!billUploadAction}
            closeHandler={() => setBillUploadAction(null)}
            reportPeriodId={billUploadAction.reportPeriodId}
            billType={billType}
            reportActionId={billUploadAction.id}
          />
        }
      </>
    )
  }